import { useEffect, useRef, useState } from 'react';

import { cn } from '@rocco/utils/cn';
import {
    BANNER_PARALLAX_SCALE,
    BANNER_SCRIM,
} from '~/ui-rocco/pages/detail-shared/banner-motion';
import { bannerExposureStyle } from '~/ui-rocco/pages/detail-shared/BannerExposure';
import {
    ProfessionalHeroCaption,
    type AudienceStat,
} from './ProfessionalHeroCaption';

/**
 * Timings taken off the reference, not guessed.
 *
 * Frames pulled at 10fps show a cut on very nearly every one, so the reel runs
 * at ten cuts a second. The body of it lasts from about 1.0s to 3.5s — two and
 * a half seconds — with the imagery scaling up out of nothing over the first
 * half-second rather than simply appearing, and collapsing back over the last
 * four-tenths.
 *
 * Two things here deliberately depart from it. The reference splits its
 * wordmark and flashes the images through the gap, which works on a five-letter
 * name and not on "Christian Anderson Architects", so the name grows instead
 * and never breaks. And the reference's reel collapses to nothing, where this
 * one hands its last frame to the banner.
 */
const FRAME_MS = 140;
const REEL_IN_MS = 500;
const REEL_MS = 1600;
/** A beat after the reel stops, so it reads as having ended rather than cut. */
const SETTLE_MS = 60;
/** The final image opening out, and the handover to the page underneath. */
const GROW_MS = 900;
const FADE_MS = 320;
/**
 * When the final image finishes opening — and so the moment every other part
 * of the sequence has to have finished too.
 *
 * The fade out and the name's growth are both derived from this rather than
 * written out, so the three cannot drift apart: a fade scheduled a beat early
 * starts the handover before the image has landed, and a name still growing
 * when the layer unmounts jumps to full size against the title beneath it.
 */
const LANDED_MS = REEL_MS + SETTLE_MS + GROW_MS;

type Phase = 'reel' | 'grow' | 'done';

/**
 * The profile's opening: the practice's name on black, their work flicking
 * past behind it, then one image opening out to the banner.
 *
 * Three beats. The name arrives small and grows for the whole sequence — the
 * reference breaks the words apart and reassembles them, which reads well on a
 * two-word title and badly on "Christian Anderson Architects", so the growth
 * carries the same build without ever splitting a word. Behind it the work
 * flicks past at eleven frames a second, fast enough to read as a reel rather
 * than a slideshow. Then the reel stops and a single frame opens from nothing
 * to fill the screen, which is where it rests.
 *
 * It plays inside the banner's own box, not over the viewport. That is what
 * makes the end state exact: the last frame opens out to fill this layer, and
 * this layer *is* the banner, so it finishes at the banner's original height
 * rather than at whatever the window happens to be. It also gives the name one
 * centre to sit in for the whole sequence instead of two that disagree.
 *
 * The last frame is the banner's own first image, so when this layer fades the
 * page beneath is already showing the same picture and nothing jumps.
 */
export const ProfessionalHeroIntro = ({
    name,
    images,
    finalImage,
    graded = false,
    discipline,
    audience,
}: {
    name: string;
    /** Small renditions — these flick past, so they are never read closely. */
    images: string[];
    /** The banner's own image, which is what the last frame opens out to. */
    finalImage?: string;
    /** The banner grades its highlights; the last frame has to match. */
    graded?: boolean;
    /**
     * The discipline and the audience set under the name, matching the
     * banner's own.
     *
     * Carried through the sequence rather than left to the banner: with no
     * counterpart here they simply appeared the instant this layer went, and
     * the name — centred with them in one group and without them in the
     * other — jumped by half their height in the same instant.
     */
    discipline?: string;
    audience: AudienceStat[];
}) => {
    const [phase, setPhase] = useState<Phase>('reel');
    const [frame, setFrame] = useState(0);
    const [reelOpen, setReelOpen] = useState(false);
    const [nameGrown, setNameGrown] = useState(false);
    const [grown, setGrown] = useState(false);
    const [gone, setGone] = useState(false);
    const timers = useRef<number[]>([]);

    useEffect(() => {
        // Nothing to show, or the reader has asked for less movement: the
        // intro is decoration, so it simply does not happen.
        if (
            images.length === 0 ||
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            setPhase('done');
            setGone(true);
            return;
        }

        const at = (ms: number, run: () => void) => {
            timers.current.push(window.setTimeout(run, ms));
        };

        // The reel scales up out of nothing rather than cutting in at size.
        at(20, () => setReelOpen(true));
        const reel = window.setInterval(
            () => setFrame((current) => current + 1),
            FRAME_MS,
        );
        // The name grows across the whole sequence, not just the last beat,
        // and lands at full size exactly as the image does.
        at(20, () => setNameGrown(true));
        at(REEL_MS, () => {
            // The reel stops here and holds its last frame; the frame it is
            // holding is what the final image opens out of.
            window.clearInterval(reel);
            setPhase('grow');
            at(SETTLE_MS, () => setGrown(true));
        });
        at(LANDED_MS, () => setPhase('done'));
        at(LANDED_MS + FADE_MS, () => setGone(true));

        return () => {
            window.clearInterval(reel);
            timers.current.forEach(window.clearTimeout);
            timers.current = [];
        };
    }, [images.length]);

    if (gone) return null;

    const finalSrc = finalImage ?? images[0];

    return (
        <div
            aria-hidden="true"
            className={cn(
                'pointer-events-none absolute inset-0 z-[60] overflow-hidden bg-black',
                'transition-opacity ease-out motion-reduce:transition-none',
                phase === 'done' ? 'opacity-0' : 'opacity-100',
            )}
            style={{ transitionDuration: `${FADE_MS}ms` }}
        >
            {/* The reel: one frame at a time, centred, small enough that the
                name reads over it.

                It stays mounted through the grow beat, underneath the opening
                image. Unmounting it at the switch leaves nothing on screen
                until the final image has scaled up off zero, and that gap
                reads as a black flash at the exact moment the sequence is
                meant to hand over. */}
            {phase !== 'done' ? (
                // On the viewport's midline, like the name over it — centred on
                // this layer the reel sat a good deal below the title, since
                // the banner is taller than the window.
                <div className="absolute inset-x-0 top-[50vh] flex -translate-y-1/2 justify-center">
                    <div
                        className="relative h-[26rem] w-[21rem] origin-center overflow-hidden rounded-tile transition-transform ease-out motion-reduce:transition-none"
                        style={{
                            transitionDuration: `${REEL_IN_MS}ms`,
                            transitionTimingFunction:
                                'cubic-bezier(0.22, 1, 0.36, 1)',
                            transform: `scale(${reelOpen ? 1 : 0.08})`,
                        }}
                    >
                        {/* Every frame is mounted once and the current one
                            shown, rather than one element having its `src`
                            swapped seven times a second: a swap that late can
                            still be waiting on a decode when the next frame is
                            due, so the reel drops cuts. Mounted together with
                            only visibility changing, a frame costs nothing.

                            These are thumbnails, not the banner renditions —
                            a frame is on screen for a seventh of a second at
                            a fraction of the width. */}
                        {images.map((src, index) => (
                            <img
                                key={src}
                                src={src}
                                alt=""
                                className="absolute inset-0 size-full object-cover"
                                style={{
                                    visibility:
                                        index === frame % images.length
                                            ? 'visible'
                                            : 'hidden',
                                }}
                            />
                        ))}
                    </div>
                </div>
            ) : null}

            {/* The last frame, opening from nothing to the banner. Scale, not
                width and height: a transform is composited, and this is the
                one moment on the page where a dropped frame would be
                obvious. */}
            {phase !== 'reel' && finalSrc ? (
                <div className="absolute inset-0 overflow-hidden">
                    <img
                        src={finalSrc}
                        alt=""
                        className="absolute inset-0 size-full origin-center object-cover transition-transform ease-out motion-reduce:transition-none"
                        style={{
                            transitionDuration: `${GROW_MS}ms`,
                            transitionTimingFunction:
                                'cubic-bezier(0.22, 1, 0.36, 1)',
                            // Two states, and both matter.
                            //
                            // It RESTS where the banner rests — scaled past
                            // its box by the parallax, about its own centre,
                            // which is the banner's centre. Anything else and
                            // the picture jumps as this layer fades. That is
                            // why the growth is not simply given a different
                            // transform-origin: an origin that made it grow
                            // from the right place would land it in the wrong
                            // one.
                            //
                            // It GROWS from the viewport's midline instead,
                            // where the reel just was. The banner is taller
                            // than the window, so its centre sits well below
                            // that; collapsed to a point at its own centre the
                            // frame appeared to come up out of the lower half
                            // of the screen. The translate carries the point
                            // up by exactly the difference — half the banner
                            // less half the window — and unwinds to zero as it
                            // grows.
                            transform: grown
                                ? `translateY(0) scale(${BANNER_PARALLAX_SCALE})`
                                : 'translateY(calc(50vh - 50%)) scale(0)',
                            // Same grade, for the same reason: ungraded, the
                            // highlights changed at the handover.
                            ...(graded ? bannerExposureStyle : null),
                        }}
                    />
                    {/* The banner's own 10% black. */}
                    <div className="absolute inset-0 bg-black/10" />
                    {/* And the same scrim, which otherwise arrives with the
                        banner and reads as part of the jump. It fades in over
                        the grow rather than being there from the start, so it
                        does not darken an image the size of a postage stamp. */}
                    <div
                        className={cn(
                            BANNER_SCRIM,
                            'transition-opacity ease-out motion-reduce:transition-none',
                        )}
                        style={{
                            transitionDuration: `${GROW_MS}ms`,
                            opacity: grown ? undefined : 0,
                        }}
                    />
                </div>
            ) : null}

            {/* The name and its discipline, above all of it, growing as one
                group so the two keep their spacing through the sequence. */}
            {/* On the viewport's midline, which is where the banner puts its
                own title — the banner is taller than the window, so centring
                on the layer itself would leave the name low and then jump it
                as this hands over. */}
            <div className="absolute inset-x-0 top-[50vh] flex -translate-y-1/2 justify-center px-[3rem]">
                <div
                    className="origin-center transition-transform ease-out motion-reduce:transition-none"
                    style={{
                        transitionDuration: `${LANDED_MS}ms`,
                        transitionTimingFunction:
                            'cubic-bezier(0.22, 1, 0.36, 1)',
                        transform: `scale(${nameGrown ? 1 : 0.72})`,
                    }}
                >
                    <p
                        className={cn(
                            'm-0 max-w-[68rem] text-center',
                            'font-gestura font-normal text-white',
                            // The hero's own title metrics, to the decimal:
                            // this element fades out over that one, and a size
                            // or leading that differs by even a little reads as
                            // the name shifting as the intro ends.
                            'max-md:text-[2.3287rem] max-md:leading-[1.02] md:text-[7.5rem] md:leading-[1.08]',
                            'tracking-[-0.045em]',
                        )}
                    >
                        {name}
                    </p>
                    {/* Arrives with the final image rather than after it,
                        so it has settled before this layer hands over to the
                        banner's own copy: the reveal is shorter than the
                        grow it runs under. */}
                    <ProfessionalHeroCaption
                        discipline={discipline}
                        audience={audience}
                        revealed={grown}
                    />
                </div>
            </div>
        </div>
    );
};
